import type { Dashboard } from "@/data/dashboards";

/**
 * SIM-1418: selected values per tenant property key. A dashboard passes a property's filter if any
 * of its tag values for that key match any selected value — OR within a property, AND across
 * properties; empty keys impose no constraint. SIM-1420: `school_year` and `category` flow through
 * this same shape, their standalone fields removed once PropertyDef became canonical.
 */
export type SelectedPropertyValues = Record<string, string[]>;

export interface FilterOptions {
  searchQuery: string;
  selectedGroupId: string | null;
  groupDashboardIds: string[] | undefined;
  starredIds: string[];
  selectedPropertyValues?: SelectedPropertyValues;
}

/**
 * Does this dashboard satisfy every per-property filter group? A property
 * group passes if the dashboard carries at least one tag value for that
 * key matching any of the user's selected values.
 */
function matchesPropertyFilters(dashboard: Dashboard, selected: SelectedPropertyValues): boolean {
  for (const [key, values] of Object.entries(selected)) {
    if (values.length === 0) continue;
    const tagsForKey = dashboard.tags.filter((t) => t.propertyKey === key).map((t) => t.value);
    const hit = tagsForKey.some((v) => values.includes(v));
    if (!hit) return false;
  }
  return true;
}

/**
 * Filter and sort dashboards based on user-selected criteria.
 *
 * Sort order: archived last → starred first → school year ascending → title alpha
 */
export function filterAndSortDashboards(
  dashboards: Dashboard[],
  options: FilterOptions,
): Dashboard[] {
  const {
    searchQuery,
    selectedGroupId,
    groupDashboardIds,
    starredIds,
    selectedPropertyValues = {},
  } = options;

  return dashboards
    .filter((d) => {
      const matchesSearch =
        !searchQuery ||
        d.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.school_year.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesGroup =
        !selectedGroupId || !groupDashboardIds || groupDashboardIds.includes(d.id);
      const matchesProps = matchesPropertyFilters(d, selectedPropertyValues);
      return matchesSearch && matchesGroup && matchesProps;
    })
    .sort((a, b) => {
      if (a.archived !== b.archived) return a.archived ? 1 : -1;
      const aStarred = starredIds.includes(a.id);
      const bStarred = starredIds.includes(b.id);
      if (aStarred !== bStarred) return aStarred ? -1 : 1;
      const yearCmp = a.school_year.localeCompare(b.school_year);
      if (yearCmp !== 0) return yearCmp;
      return a.title.localeCompare(b.title);
    });
}

/**
 * Count the number of active filters for the badge display. SIM-1420 (slice 4) — year/category fold
 * into selectedPropertyValues; the standalone selectedSchoolYears/selectedCategories args were
 * removed when those state slices were dropped from the Index page.
 */
export function activeFilterCount(
  selectedGroupId: string | null,
  searchQuery: string,
  selectedPropertyValues: SelectedPropertyValues = {},
): number {
  const propCount = Object.values(selectedPropertyValues).reduce((sum, vs) => sum + vs.length, 0);
  return (selectedGroupId ? 1 : 0) + (searchQuery ? 1 : 0) + propCount;
}
